import { useState, useCallback } from 'react';
import employeeHistoryAPI from '../api/employee-history';

/**
 * Hook para gestión de Historial de Personal
 */
export default function useEmployeeHistory(employeeId = null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    categoria: '',
    tipo_evento: '',
    fecha_desde: '',
    fecha_hasta: '',
    activo: 'true'
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 100,
    offset: 0
  });

  // ═══════════════════════════════════════════════════════════════
  // CARGAR DATOS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Cargar historial de toda la empresa con filtros
   */
  const loadCompanyHistory = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const filterParams = { ...filters, ...customFilters };
      const result = await employeeHistoryAPI.getCompanyHistory(filterParams);

      if (result.success) {
        setHistory(result.history || []);
        setPagination({
          total: result.total || 0,
          limit: result.limit || 100,
          offset: result.offset || 0
        });
        return { success: true, history: result.history };
      } else {
        setError('Error al cargar historial');
        return { success: false, error: 'Error al cargar historial' };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar historial';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Cargar historial de un empleado con filtros
   */
  const loadHistory = useCallback(async (empId = employeeId, customFilters = {}) => {
    if (!empId) {
      console.warn('No employeeId provided to loadHistory');
      return { success: false, error: 'Employee ID required' };
    }

    setLoading(true);
    setError(null);

    try {
      const filterParams = { ...filters, ...customFilters };
      const result = await employeeHistoryAPI.getEmployeeHistory(empId, filterParams);

      if (result.success) {
        setHistory(result.history || []);
        setPagination({
          total: result.total || 0,
          limit: result.limit || 100,
          offset: result.offset || 0
        });
        return { success: true, history: result.history };
      } else {
        setError('Error al cargar historial');
        return { success: false, error: 'Error al cargar historial' };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar historial';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [employeeId, filters]);

  /**
   * Cargar timeline (vista cronológica)
   */
  const loadTimeline = useCallback(async (empId = employeeId) => {
    if (!empId) {
      return { success: false, error: 'Employee ID required' };
    }

    setLoading(true);
    setError(null);

    try {
      const result = await employeeHistoryAPI.getTimeline(empId);

      if (result.success) {
        setTimeline(result.timeline || []);
        return { success: true, timeline: result.timeline };
      } else {
        return { success: false, error: 'Error al cargar timeline' };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  /**
   * Cargar estadísticas del historial
   */
  const loadStats = useCallback(async (empId = employeeId) => {
    if (!empId) {
      return { success: false, error: 'Employee ID required' };
    }

    setLoading(true);
    setError(null);

    try {
      const result = await employeeHistoryAPI.getStats(empId);

      if (result.success) {
        setStats(result.stats);
        return { success: true, stats: result.stats };
      } else {
        return { success: false, error: 'Error al cargar estadísticas' };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  /**
   * Obtener un evento específico
   */
  const getEvent = useCallback(async (eventId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await employeeHistoryAPI.getEvent(eventId);

      if (result.success) {
        return { success: true, event: result.event };
      } else {
        return { success: false, error: 'Error al cargar evento' };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // CREAR Y MODIFICAR
  // ═══════════════════════════════════════════════════════════════

  /**
   * Crear nuevo evento
   */
  const createEvent = useCallback(async (eventData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await employeeHistoryAPI.createEvent(eventData);

      if (result.success) {
        // Recargar historial si tenemos employeeId
        if (employeeId) {
          await loadHistory();
        }
        return { success: true, event: result.event };
      } else {
        const errorMsg = result.message || 'Error al crear evento';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [employeeId, loadHistory]);

  /**
   * Actualizar evento
   */
  const updateEvent = useCallback(async (eventId, eventData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await employeeHistoryAPI.updateEvent(eventId, eventData);

      if (result.success) {
        // Recargar historial si tenemos employeeId
        if (employeeId) {
          await loadHistory();
        }
        return { success: true, event: result.event };
      } else {
        const errorMsg = result.message || 'Error al actualizar evento';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [employeeId, loadHistory]);

  /**
   * Inactivar evento
   */
  const inactivateEvent = useCallback(async (eventId, motivo) => {
    if (!motivo) {
      return { success: false, error: 'Debe proporcionar un motivo' };
    }

    setLoading(true);
    setError(null);

    try {
      const result = await employeeHistoryAPI.inactivateEvent(eventId, motivo);

      if (result.success) {
        // Recargar historial si tenemos employeeId
        if (employeeId) {
          await loadHistory();
        }
        return { success: true };
      } else {
        const errorMsg = result.message || 'Error al inactivar evento';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [employeeId, loadHistory]);

  /**
   * Reactivar evento
   */
  const reactivateEvent = useCallback(async (eventId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await employeeHistoryAPI.reactivateEvent(eventId);

      if (result.success) {
        // Recargar historial si tenemos employeeId
        if (employeeId) {
          await loadHistory();
        }
        return { success: true };
      } else {
        const errorMsg = result.message || 'Error al reactivar evento';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [employeeId, loadHistory]);

  // ═══════════════════════════════════════════════════════════════
  // HELPERS DE REGISTRO RÁPIDO
  // ═══════════════════════════════════════════════════════════════

  const registrarIngreso = useCallback(async (empId, data) => {
    return employeeHistoryAPI.registrarIngreso(empId, data);
  }, []);

  const registrarPromocion = useCallback(async (empId, data) => {
    return employeeHistoryAPI.registrarPromocion(empId, data);
  }, []);

  const registrarAumentoSalarial = useCallback(async (empId, data) => {
    return employeeHistoryAPI.registrarAumentoSalarial(empId, data);
  }, []);

  const registrarCambioDepartamento = useCallback(async (empId, data) => {
    return employeeHistoryAPI.registrarCambioDepartamento(empId, data);
  }, []);

  const registrarAccionDisciplinaria = useCallback(async (empId, data) => {
    return employeeHistoryAPI.registrarAccionDisciplinaria(empId, data);
  }, []);

  const registrarReconocimiento = useCallback(async (empId, data) => {
    return employeeHistoryAPI.registrarReconocimiento(empId, data);
  }, []);

  const registrarCapacitacion = useCallback(async (empId, data) => {
    return employeeHistoryAPI.registrarCapacitacion(empId, data);
  }, []);

  const registrarTerminacion = useCallback(async (empId, data) => {
    return employeeHistoryAPI.registrarTerminacion(empId, data);
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Obtener filtros disponibles
   */
  const getAvailableFilters = useCallback(async () => {
    try {
      const result = await employeeHistoryAPI.getAvailableFilters();
      return result;
    } catch (err) {
      console.error('Error al obtener filtros:', err);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Actualizar filtros
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Limpiar filtros
   */
  const clearFilters = useCallback(() => {
    setFilters({
      categoria: '',
      tipo_evento: '',
      fecha_desde: '',
      fecha_hasta: '',
      activo: 'true'
    });
  }, []);

  return {
    // Estado
    loading,
    error,
    history,
    timeline,
    stats,
    filters,
    pagination,

    // Cargar datos
    loadCompanyHistory,
    loadHistory,
    loadTimeline,
    loadStats,
    getEvent,

    // Crear y modificar
    createEvent,
    updateEvent,
    inactivateEvent,
    reactivateEvent,

    // Helpers de registro rápido
    registrarIngreso,
    registrarPromocion,
    registrarAumentoSalarial,
    registrarCambioDepartamento,
    registrarAccionDisciplinaria,
    registrarReconocimiento,
    registrarCapacitacion,
    registrarTerminacion,

    // Utilidades
    getAvailableFilters,
    updateFilters,
    clearFilters
  };
}
